import React, {useEffect} from 'react';

/**
 * UserThemeProvider
 * - Busca CSS variables do tema escolhido pelo usuário autenticado
 * - Injeta as variáveis em um <style id="user-theme-style"> no <head>
 * - Carrega Google Font se o tema exigir
 */
export function UserThemeProvider() {
  useEffect(() => {
    let aborted = false;

    async function loadUserTheme() {
      try {
        const url = `/api/v1/users/me/theme/css?t=${Date.now()}`;
        const res = await fetch(url, {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
          },
          cache: 'no-store',
        });
        if (!res.ok) return;
        const data: {cssVariables?: string; isGoogleFont?: boolean; fontFamily?: string} = await res.json();
        if (aborted) return;

        // Inject CSS variables with highest precedence: inline on :root
        const css = (data?.cssVariables || '').trim();
        const root = document.documentElement;
        // clear previous inline vars that we manage (optional)
        // apply new ones
        if (css) {
          css
            .split(';')
            .map(s => s.trim())
            .filter(Boolean)
            .forEach(pair => {
              const [name, ...rest] = pair.split(':');
              if (name && name.startsWith('--')) {
                const value = rest.join(':').trim();
                if (value) root.style.setProperty(name.trim(), value);
              }
            });
        }

        // Load Google font if needed
        const fontFamily = (data?.fontFamily || '').trim();
        const isGoogle = !!data?.isGoogleFont && !!fontFamily;
        const linkId = 'user-theme-google-font';
        let linkEl = document.getElementById(linkId) as HTMLLinkElement | null;
        if (isGoogle) {
          const familyParam = encodeURIComponent(fontFamily + ':wght@400;500;600;700');
          const href = `https://fonts.googleapis.com/css2?family=${familyParam}&display=swap`;
          if (!linkEl) {
            linkEl = document.createElement('link');
            linkEl.id = linkId;
            linkEl.rel = 'stylesheet';
            linkEl.href = href;
            document.head.appendChild(linkEl);
          } else if (linkEl.href !== href) {
            linkEl.href = href;
          }
        } else if (linkEl) {
          // remove previous google font if theme no longer needs it
          linkEl.parentElement?.removeChild(linkEl);
        }

        // Toggle dark class based on selected theme
        if (typeof (data as any)?.isDark === 'boolean') {
          const isDark = !!(data as any).isDark;
          root.classList.toggle('dark', isDark);
          root.setAttribute('data-theme', isDark ? 'dark' : 'light');
        }
      } catch (e) {
        // fail silently
      }
    }

    const handler = () => {
      if (!aborted) {
        loadUserTheme();
      }
    };

    // initial load
    loadUserTheme();
    // listen for manual reloads after user changes theme
    window.addEventListener('user-theme:reload', handler);
    return () => {
      aborted = true;
      window.removeEventListener('user-theme:reload', handler);
    };
  }, []);

  return null;
}
